import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsOfServicePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-2xl mx-auto px-6 py-8">
        <button data-testid="tos-back-btn" onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-muted text-sm mb-6 hover:text-espresso transition-colors">
          <ArrowLeft size={16} /> Back
        </button>

        <h1 data-testid="tos-title" className="font-heading text-3xl text-espresso mb-2">Terms of Service</h1>
        <p className="text-text-muted text-sm mb-8">Last updated: February 2026</p>

        <div className="space-y-8 text-text-muted leading-relaxed text-sm">
          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">1. Acceptance of Terms</h2>
            <p>By accessing or using the HostBase guest portal ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service. The Service is a digital concierge platform that provides property information, guest services, and in-property shopping for short-term rental guests.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">2. Eligibility</h2>
            <p>You must be at least 18 years of age to use this Service. By using the Service, you represent and warrant that you are at least 18 years old and have the legal capacity to enter into these Terms. Access to the Service is limited to registered guests with valid reservations as authorized by the property owner.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">3. Account & Authentication</h2>
            <p className="mb-3">Access to the Service requires verification via a one-time passcode (OTP) sent to your registered mobile phone number. You are responsible for:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Providing an accurate phone number</li>
              <li>Maintaining the security of your authentication session</li>
              <li>All activities that occur under your session</li>
              <li>Notifying the property manager immediately if you suspect unauthorized access</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">4. Services & Products</h2>
            <p className="mb-3">The Service allows you to browse and purchase guest services (e.g., laundry, cleaning, transportation) and in-property products ("Shop the House"). By placing an order:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>You agree to pay the listed price plus any applicable taxes or fees</li>
              <li>Service availability and fulfillment times are subject to property manager availability</li>
              <li>Product availability is subject to current inventory and may be limited</li>
              <li>Prices are listed in US Dollars and are subject to change without notice</li>
              <li>The property owner reserves the right to refuse or cancel any order</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">5. Payments & Refunds</h2>
            <p className="mb-3">All payments are processed securely through Stripe. By making a purchase:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>You authorize the charge to your selected payment method</li>
              <li>All sales are final unless otherwise specified by the property manager</li>
              <li>Refund requests must be directed to the property manager within 24 hours of purchase</li>
              <li>Refunds, if approved, will be processed to the original payment method within 5-10 business days</li>
              <li>The property owner reserves the right to approve or deny refund requests at their discretion</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">6. Property Rules & Guest Conduct</h2>
            <p className="mb-3">As a guest, you agree to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Follow all property rules and guidelines provided through the Service</li>
              <li>Treat the property, its contents, and surrounding areas with respect</li>
              <li>Not use the Service for any unlawful purpose</li>
              <li>Not attempt to access areas of the Service not intended for your use</li>
              <li>Report any damage, maintenance issues, or safety concerns promptly</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">7. SMS Communications</h2>
            <p>By using the Service, you consent to receive SMS messages including authentication codes, order confirmations, and property-related notifications. Message and data rates may apply based on your mobile carrier plan. Message frequency varies based on your usage. You may opt out of non-essential messages by contacting the property manager.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">8. Intellectual Property</h2>
            <p>All content, features, and functionality of the Service, including but not limited to text, graphics, logos, and software, are the property of HostBase or its licensors and are protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works from any content without prior written consent.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">9. Limitation of Liability</h2>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, HOSTBASE AND THE PROPERTY OWNER SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID THROUGH THE SERVICE IN THE 30 DAYS PRECEDING THE CLAIM.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">10. Disclaimer of Warranties</h2>
            <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">11. Indemnification</h2>
            <p>You agree to indemnify, defend, and hold harmless HostBase, the property owner, and their respective officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from your use of the Service or violation of these Terms.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">12. Modifications to Terms</h2>
            <p>We reserve the right to modify these Terms at any time. Changes will be effective upon posting to the Service. Your continued use of the Service after changes are posted constitutes your acceptance of the modified Terms. We encourage you to review these Terms periodically.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">13. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of the State in which the property is located, without regard to conflict of law principles. Any disputes arising under these Terms shall be resolved in the courts of that jurisdiction.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">14. Contact</h2>
            <p>For questions regarding these Terms of Service, please contact the property manager through the guest portal or at the contact information provided during your stay.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
